'use client';

import { Link } from '@/i18n/navigation';
import { useState } from 'react';
import { useForm, Controller } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { toast } from 'sonner';
import { useLocale, useTranslations } from 'next-intl';

import { createLeadSchema, type LeadInput } from '@/lib/schemas/lead';
import { track } from '@/lib/analytics/ym';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { buttonVariants } from '@/components/ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { cn } from '@/lib/utils';
import { FadeUp } from '@/components/motion';

const BUDGETS: NonNullable<LeadInput['budget']>[] = [
  '<500K',
  '500K-1M',
  '1M-3M',
  '3M+',
  'undefined',
];

const STEPS = ['reply', 'call', 'audit'] as const;

function FieldError({ message }: { message?: string }) {
  if (!message) return null;
  return <p className="text-sm text-destructive">{message}</p>;
}

export function LeadForm() {
  const t = useTranslations('LeadForm');
  const locale = useLocale();
  const [submitting, setSubmitting] = useState(false);
  const [started, setStarted] = useState(false);

  function fireStart() {
    if (started) return;
    setStarted(true);
    track.formStart('lead');
  }

  const {
    register,
    handleSubmit,
    control,
    reset,
    formState: { errors },
  } = useForm<LeadInput>({
    resolver: zodResolver(
      createLeadSchema({
        nameMax: t('errors.nameMax'),
        companyMax: t('errors.companyMax'),
        painMin: t('errors.painMin'),
        painMax: t('errors.painMax'),
        contactMin: t('errors.contactMin'),
        contactInvalid: t('errors.contactInvalid'),
        consent: t('errors.consent'),
      }),
    ),
    mode: 'onTouched',
    defaultValues: {
      name: '',
      company: '',
      pain: '',
      contact: '',
    },
  });

  const onInvalid = (errs: typeof errors) => {
    track.formError('lead', Object.keys(errs));
  };

  const onSubmit = async (data: LeadInput) => {
    setSubmitting(true);
    try {
      const res = await fetch('/api/lead', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ...data, locale }),
      });
      if (res.status === 429) {
        toast.warning(t('toast.rateLimited'));
        return;
      }
      if (!res.ok) {
        toast.error(t('toast.failed'));
        return;
      }
      toast.success(t('toast.success'));
      track.formSubmit({
        form: 'lead',
        budget: data.budget,
      });
      reset();
      setStarted(false);
    } catch {
      toast.error(t('toast.failed'));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section
      id="lead"
      className="bg-background container mx-auto max-w-2xl px-4 md:px-6 lg:px-8 py-24 md:py-32"
    >
      <FadeUp>
        <p className="text-xs uppercase tracking-wider text-muted-foreground">
          {t('eyebrow')}
        </p>
      </FadeUp>
      <FadeUp delay={0.1}>
        <h2 className="mt-3 text-3xl md:text-4xl font-bold tracking-tight">
          {t('heading')}
        </h2>
      </FadeUp>
      <FadeUp delay={0.2}>
        <p className="mt-4 text-base md:text-lg text-muted-foreground">
          {t('subhead')}
        </p>
      </FadeUp>

      <FadeUp delay={0.3}>
        <ol className="mt-8 space-y-3 text-sm md:text-base">
          {STEPS.map((key, idx) => (
            <li key={key} className="flex gap-3">
              <span
                aria-hidden="true"
                className="inline-flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-muted text-xs font-semibold"
              >
                {idx + 1}
              </span>
              <span>{t(`steps.${key}`)}</span>
            </li>
          ))}
        </ol>
      </FadeUp>

      <FadeUp delay={0.4}>
        <form
          onSubmit={handleSubmit(onSubmit, onInvalid)}
          onFocus={fireStart}
          className="mt-10 space-y-5"
          noValidate
        >
          <div className="grid gap-2">
            <Label htmlFor="lf-name">{t('fields.name.label')}</Label>
            <Input
              id="lf-name"
              placeholder={t('fields.name.placeholder')}
              autoComplete="name"
              aria-invalid={!!errors.name}
              {...register('name')}
            />
            <FieldError message={errors.name?.message} />
          </div>

          <div className="grid gap-2">
            <Label htmlFor="lf-company">{t('fields.company.label')}</Label>
            <Input
              id="lf-company"
              placeholder={t('fields.company.placeholder')}
              autoComplete="organization"
              aria-invalid={!!errors.company}
              {...register('company')}
            />
            <FieldError message={errors.company?.message} />
          </div>

          <div className="grid gap-2">
            <Label htmlFor="lf-budget">{t('fields.budget.label')}</Label>
            <Controller
              control={control}
              name="budget"
              render={({ field }) => (
                <Select value={field.value ?? ''} onValueChange={field.onChange}>
                  <SelectTrigger
                    id="lf-budget"
                    className="w-full"
                    aria-invalid={!!errors.budget}
                  >
                    <SelectValue placeholder={t('fields.budget.placeholder')} />
                  </SelectTrigger>
                  <SelectContent>
                    {BUDGETS.map((value) => (
                      <SelectItem key={value} value={value}>
                        {t(`budgets.${value}`)}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}
            />
            <FieldError message={errors.budget?.message} />
          </div>

          <div className="grid gap-2">
            <Label htmlFor="lf-pain">{t('fields.pain.label')}</Label>
            <Textarea
              id="lf-pain"
              rows={4}
              placeholder={t('fields.pain.placeholder')}
              aria-invalid={!!errors.pain}
              {...register('pain')}
            />
            <p className="text-xs text-muted-foreground">{t('fields.pain.hint')}</p>
            <FieldError message={errors.pain?.message} />
          </div>

          <div className="grid gap-2">
            <Label htmlFor="lf-contact">{t('fields.contact.label')}</Label>
            <Input
              id="lf-contact"
              placeholder={t('fields.contact.placeholder')}
              aria-invalid={!!errors.contact}
              {...register('contact')}
            />
            <p className="text-xs text-muted-foreground">
              {t('fields.contact.hint')}
            </p>
            <FieldError message={errors.contact?.message} />
          </div>

          <div className="flex items-start gap-3">
            <input
              id="lf-consent"
              type="checkbox"
              className="mt-1 h-4 w-4 shrink-0 rounded border-input accent-foreground"
              aria-invalid={!!errors.consent}
              {...register('consent')}
            />
            <Label
              htmlFor="lf-consent"
              className="text-sm text-muted-foreground leading-snug"
            >
              <span>
                {t.rich('consent', {
                  law: (chunks) => (
                    <Link
                      href="/privacy"
                      className="underline underline-offset-4 hover:text-foreground"
                    >
                      {chunks}
                    </Link>
                  ),
                })}
              </span>
            </Label>
          </div>
          <FieldError message={errors.consent?.message} />

          <button
            type="submit"
            disabled={submitting}
            className={cn(
              buttonVariants({ size: 'lg' }),
              'min-h-12 w-full text-base md:w-auto rounded-full',
            )}
          >
            {submitting ? t('submitting') : t('submit')}
          </button>
        </form>
      </FadeUp>
    </section>
  );
}
